import { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  CartesianGrid,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  Legend,
} from 'recharts';
import { Sparkles, MessageCircle } from 'lucide-react';
import {
  creativeWorkbenchApi,
  type WorkbenchFitScatterItem,
  type WorkbenchFitScatterResponse,
  type WorkbenchVerdict,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import type { ChatSeedInput } from './EmbeddedChatPanel';

const VERDICT_COLOR: Record<WorkbenchVerdict, string> = {
  strong: '#10b981',
  promising: '#14b8a6',
  needs_work: '#f59e0b',
  misaligned: '#ef4444',
};

interface Props {
  onAdSelect: (ad: WorkbenchFitScatterItem) => void;
  onDrillDown: (seed: ChatSeedInput) => void;
}

export function FitVsPerformanceScatter({ onAdSelect, onDrillDown }: Props) {
  const { clientUid, personaId, dateRange } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchFitScatterResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [hoverItem, setHoverItem] = useState<WorkbenchFitScatterItem | null>(null);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getFitScatter({ clientUid, personaId: personaId ?? null })
      .then((p) => {
        if (!cancelled) setData(p);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid, personaId]);

  // Group items by verdict so each verdict gets its own legend entry/color.
  const grouped = useMemo(() => {
    const verdicts: WorkbenchVerdict[] = ['strong', 'promising', 'needs_work', 'misaligned'];
    return verdicts.map((v) => ({
      verdict: v,
      items: (data?.items ?? []).filter((i) => i.verdict === v),
    }));
  }, [data]);

  const r2 = data?.trendline.r_squared ?? 0;

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
            <Sparkles size={14} className="text-teal-500" />
            Fit vs Performance
          </h2>
          <p className="text-[10px] text-slate-400 mt-0.5">
            Each dot is an ad. Higher fit (right) → higher LTV-ROAS (up).
            Bubble size = spend. {r2 > 0 && (
              <span className="text-teal-600 font-bold">
                Fit explains {(r2 * 100).toFixed(0)}% of ROAS variance
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(['strong', 'promising', 'needs_work', 'misaligned'] as WorkbenchVerdict[]).map(
            (v) => (
              <span
                key={v}
                className="inline-flex items-center gap-1 text-[10px] font-mono text-slate-500"
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: VERDICT_COLOR[v] }}
                />
                {v.replace('_', ' ')}
              </span>
            ),
          )}
        </div>
      </header>

      {data && (
        <div className="grid grid-cols-4 gap-2 text-center">
          <QuadrantStat
            label="Aligned & Winning"
            value={data.quadrants.aligned_winning}
            color="bg-emerald-50 text-emerald-700"
          />
          <QuadrantStat
            label="Aligned, Failing"
            value={data.quadrants.aligned_failing}
            color="bg-amber-50 text-amber-700"
          />
          <QuadrantStat
            label="Lucky Misaligned"
            value={data.quadrants.lucky_misaligned}
            color="bg-sky-50 text-sky-700"
          />
          <QuadrantStat
            label="Drop / Pause"
            value={data.quadrants.drop_pause}
            color="bg-red-50 text-red-700"
          />
        </div>
      )}

      <div className="h-80 relative">
        {loading && !data && (
          <div className="text-xs text-slate-400 h-full flex items-center justify-center">
            Loading scatter...
          </div>
        )}
        {!loading && data && data.items.length === 0 && (
          <div className="text-xs text-slate-400 h-full flex items-center justify-center">
            No scoring data yet — wait for the next CreativeReview pass.
          </div>
        )}
        {data && data.items.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                type="number"
                dataKey="fit_score"
                name="Fit"
                domain={[0, 10]}
                stroke="#94a3b8"
                fontSize={10}
                ticks={[0, 2, 4, 6, 8, 10]}
                label={{
                  value: 'Persona-fit score',
                  position: 'insideBottom',
                  offset: -4,
                  fontSize: 10,
                  fill: '#64748b',
                }}
              />
              <YAxis
                type="number"
                dataKey="ltv_roas"
                name="LTV ROAS"
                stroke="#94a3b8"
                fontSize={10}
                label={{
                  value: 'LTV-ROAS',
                  angle: -90,
                  position: 'insideLeft',
                  fontSize: 10,
                  fill: '#64748b',
                }}
              />
              <ZAxis type="number" dataKey="spend" range={[40, 600]} name="Spend" />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                content={<ScatterTooltip />}
              />
              <ReferenceLine x={6} stroke="#94a3b8" strokeDasharray="3 3" />
              <ReferenceLine y={1} stroke="#94a3b8" strokeDasharray="3 3" />
              <ReferenceArea
                x1={6}
                x2={10}
                y1={1}
                y2={Number.MAX_SAFE_INTEGER}
                fill="#10b981"
                fillOpacity={0.04}
              />
              <ReferenceArea
                x1={0}
                x2={6}
                y1={0}
                y2={1}
                fill="#ef4444"
                fillOpacity={0.04}
              />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {grouped.map((g) => (
                <Scatter
                  key={g.verdict}
                  name={g.verdict.replace('_', ' ')}
                  data={g.items}
                  fill={VERDICT_COLOR[g.verdict]}
                  fillOpacity={0.7}
                  stroke={VERDICT_COLOR[g.verdict]}
                  onClick={(point) => {
                    const item = point as WorkbenchFitScatterItem;
                    onAdSelect(item);
                  }}
                  onMouseEnter={(point) =>
                    setHoverItem(point as WorkbenchFitScatterItem)
                  }
                  onMouseLeave={() => setHoverItem(null)}
                  cursor="pointer"
                />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        )}
      </div>

      {hoverItem && (
        <div className="text-[10px] text-slate-500 -mt-2">
          {hoverItem.ad_name ?? hoverItem.ad_id} • {hoverItem.target_persona ?? '—'}
        </div>
      )}

      <footer className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <button
          onClick={() =>
            onDrillDown({
              question:
                'Which ads sit in the top-right quadrant? Why are they working?',
              chart_axis: { series: 'fit_vs_roas', dimension: 'verdict' },
              date_range: { from: dateRange.from, to: dateRange.to },
            })
          }
          className="text-[10px] font-mono text-slate-500 hover:text-teal-600 inline-flex items-center gap-1"
        >
          <MessageCircle size={10} />
          Ask the analyst
        </button>
      </footer>
    </section>
  );
}

function QuadrantStat({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className={`rounded-lg p-2 ${color}`}>
      <div className="text-base font-bold tabular-nums">{value}</div>
      <div className="text-[9px] uppercase tracking-wide font-mono">{label}</div>
    </div>
  );
}

function ScatterTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload as WorkbenchFitScatterItem;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-2 text-[11px] shadow-md max-w-xs">
      <div className="font-bold text-navy-900 mb-1 truncate">
        {item.ad_name ?? item.ad_id}
      </div>
      <div className="text-slate-500 grid grid-cols-2 gap-1">
        <span>Fit</span>
        <span className="text-right text-navy-900 font-mono">
          {item.fit_score.toFixed(2)}
        </span>
        <span>Verdict</span>
        <span className="text-right text-navy-900 font-mono">{item.verdict}</span>
        <span>Target</span>
        <span className="text-right text-navy-900 truncate">
          {item.target_persona ?? '—'}
        </span>
        <span>Actual</span>
        <span className="text-right text-navy-900 truncate">
          {item.actual_persona ?? '—'}
        </span>
        <span>Spend</span>
        <span className="text-right text-navy-900 font-mono">
          ${item.spend.toLocaleString()}
        </span>
        <span>LTV ROAS</span>
        <span className="text-right text-navy-900 font-mono">
          {item.ltv_roas.toFixed(2)}x
        </span>
      </div>
      <div className="text-[10px] text-teal-600 mt-1">Click to inspect →</div>
    </div>
  );
}
